import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, Minus, Edit, Trash2, History, RefreshCw, GitMerge, Check, Search, X, Layers, Users, Clock, AlertTriangle, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtCur = (n) => `₹${fmt(n)}`;

const getErrMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return 'Failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join('; ');
  return String(d);
};

const CATEGORY_COLORS = {
  'Aluminium': 'bg-blue-100 text-blue-800 border-blue-200',
  'Stainless Steel': 'bg-purple-100 text-purple-800 border-purple-200',
  'CRCA': 'bg-slate-100 text-slate-700 border-slate-200',
  'Brass': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Copper': 'bg-orange-100 text-orange-800 border-orange-200',
  'HRCA': 'bg-teal-100 text-teal-800 border-teal-200',
  'GI': 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

function PriceTrendBadge({ history }) {
  if (!history || history.length < 2) return null;
  const last = history[history.length - 1].price;
  const prev = history[history.length - 2].price;
  const pct = ((last - prev) / prev * 100);
  if (pct > 0) return <span className="flex items-center gap-0.5 text-red-500 text-xs font-mono font-semibold"><TrendingUp size={12} />+{pct.toFixed(1)}%</span>;
  if (pct < 0) return <span className="flex items-center gap-0.5 text-green-500 text-xs font-mono font-semibold"><TrendingDown size={12} />{pct.toFixed(1)}%</span>;
  return <span className="flex items-center gap-0.5 text-slate-400 text-xs font-mono"><Minus size={12} />0%</span>;
}

function MiniSparkline({ history }) {
  if (!history || history.length < 2) return <div className="h-8" />;
  const data = history.slice(-10).map((h, i) => ({ i, price: h.price }));
  const isUp = data[data.length - 1].price >= data[0].price;
  return (
    <ResponsiveContainer width="100%" height={32}>
      <LineChart data={data}><Line type="monotone" dataKey="price" stroke={isUp ? '#f97316' : '#22c55e'} strokeWidth={1.5} dot={false} /></LineChart>
    </ResponsiveContainer>
  );
}

/** Most recent market-price update date from a price_history array (null if none) */
function lastUpdatedDate(history) {
  if (!history || history.length === 0) return null;
  let max = null;
  for (const h of history) {
    if (!h?.date) continue;
    const d = new Date(h.date);
    if (isNaN(d.getTime())) continue;
    if (!max || d > max) max = d;
  }
  return max;
}

// Market prices should be refreshed at least every 15 days; past that a grade is "due".
const UPDATE_CYCLE_DAYS = 15;
// The "Pending Market Price Updates" button only appears when at least one grade hasn't been
// updated in over a month.
const PENDING_THRESHOLD_DAYS = 30;

/** Small banner: when this RM's MARKET RM price was last updated. Amber once due (>15d), red if
 *  very stale (>30d). Uses the RM (raw) price history only, matching the dashboard's stale count. */
function LastUpdatedBadge({ history, entry }) {
  const last = lastUpdatedDate(entry ? entry?.raw?.price_history : history);
  if (!last) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border text-red-600 bg-red-50 border-red-200"
        title="Market price has never been updated">
        <AlertTriangle size={10} /> Never updated
      </span>
    );
  }
  const days = Math.floor((Date.now() - last.getTime()) / 86400000);
  const stale = days >= UPDATE_CYCLE_DAYS;  // red once not updated for 15+ days
  const rel = days <= 0 ? 'today' : days === 1 ? '1d ago' : `${days}d ago`;
  const full = last.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const cls = stale ? 'text-red-600 bg-red-50 border-red-200'
    : 'text-slate-500 bg-slate-50 border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 border ${cls}`}
      title={`Market price last updated ${full}${stale ? ` — ${days} days ago, overdue (not updated in 15+ days)` : ''}`}>
      {stale ? <AlertTriangle size={10} /> : <Clock size={10} />}
      Updated {rel}
    </span>
  );
}

/** Group raw+scrap materials by category+grade+thickness into unified entries */
function groupMaterials(materials) {
  const map = {};
  materials.forEach(m => {
    const thicknessKey = m.thickness_mm ? `@${m.thickness_mm}mm` : '';
    const key = `${m.category}::${m.grade}${thicknessKey}`;
    if (!map[key]) map[key] = { category: m.category, grade: m.grade, thickness_mm: m.thickness_mm || null };
    if (m.material_type === 'raw') map[key].raw = m;
    else if (m.material_type === 'scrap') map[key].scrap = m;
  });
  const grouped = {};
  Object.values(map).forEach(entry => {
    const cat = entry.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(entry);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => {
    const gradeCompare = a.grade.localeCompare(b.grade);
    if (gradeCompare !== 0) return gradeCompare;
    return (a.thickness_mm || 0) - (b.thickness_mm || 0);
  }));
  return grouped;
}

/* ====================== CATEGORY SELECT ====================== */

function CategorySelect({ value, onChange, testId }) {
  const [categories, setCategories] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    api.get('/materials/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleCreate = () => {
    const trimmed = newCat.trim();
    if (!trimmed) return;
    setCategories(prev => [...prev, trimmed].sort());
    onChange(trimmed);
    setShowNew(false);
    setNewCat('');
  };

  if (showNew) {
    return (
      <div className="flex gap-1.5">
        <input value={newCat} onChange={e => setNewCat(e.target.value)} autoFocus placeholder="New category name"
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNew(false); }}
          data-testid={`${testId}-new-input`}
          className="flex-1 border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
        <button onClick={handleCreate} className="px-2 h-9 bg-slate-900 text-white text-xs font-bold rounded-sm">Add</button>
        <button onClick={() => setShowNew(false)} className="px-2 h-9 border border-slate-300 text-slate-500 text-xs rounded-sm">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex gap-1.5">
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="flex-1 border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
        <option value="">Select</option>
        {categories.map(c => <option key={c} value={c}>{c}</option>)}
        {value && !categories.includes(value) && <option value={value}>{value}</option>}
      </select>
      <button onClick={() => setShowNew(true)} title="Create new category" data-testid={`${testId}-new-btn`}
        className="px-2 h-9 border border-dashed border-slate-300 text-slate-400 hover:text-slate-700 hover:border-slate-500 rounded-sm text-xs font-semibold transition-colors">
        + New
      </button>
    </div>
  );
}

/* ====================== CATEGORY HEADER ====================== */

function CategoryHeader({ category, canEdit, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === category) { setEditing(false); setName(category); return; }
    setSaving(true);
    try {
      await api.put('/materials/category/rename', { old_name: category, new_name: trimmed });
      toast.success(`Category renamed to "${trimmed}"`);
      onRenamed();
      setEditing(false);
    } catch (err) { toast.error(getErrMsg(err)); }
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input value={name} onChange={e => setName(e.target.value)} autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditing(false); setName(category); } }}
          data-testid={`rename-category-input-${category}`}
          className="border border-slate-300 rounded-sm h-7 px-2 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-slate-900 w-40" />
        <button onClick={handleSave} disabled={saving} data-testid={`rename-category-save-${category}`}
          className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-sm"><Check size={14} /></button>
        <button onClick={() => { setEditing(false); setName(category); }}
          className="p-1 text-slate-400 hover:bg-slate-100 rounded-sm"><X size={14} /></button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group/cat">
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${CATEGORY_COLORS[category] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
        {category}
      </span>
      {canEdit && (
        <button onClick={() => setEditing(true)} title="Rename category"
          data-testid={`rename-category-${category}`}
          className="p-1 text-slate-300 hover:text-slate-600 rounded-sm transition-colors opacity-0 group-hover/cat:opacity-100">
          <Edit size={12} />
        </button>
      )}
    </div>
  );
}

/* ====================== MODALS (unchanged) ====================== */

function PriceUpdateModal({ grade, entries, onClose, onUpdated }) {
  const { user } = useAuth();
  // Anyone who can edit scrap prices (e.g. Costing Editor) may set/create a scrap price here.
  const canEditScrap = user?.role === 'admin' || (user?.capabilities || []).includes('edit_scrap_price');
  const [prices, setPrices] = useState(() =>
    entries.map(e => ({
      raw_id: e.raw?.material_id || null,
      scrap_id: e.scrap?.material_id || null,
      thickness_mm: e.thickness_mm,
      rm_price: e.raw?.current_price?.toString() || '',
      scrap_price: e.scrap?.current_price?.toString() || '',
    }))
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // A scrap price typed into a grade that has no scrap material yet → create it on save.
  const isNewScrap = (p) => canEditScrap && !p.scrap_id && p.scrap_price !== '' && Number(p.scrap_price) > 0;

  const hasChanges = prices.some((p, i) => {
    const e = entries[i];
    return (p.raw_id && Number(p.rm_price) !== e.raw?.current_price) ||
           (p.scrap_id && Number(p.scrap_price) !== e.scrap?.current_price) ||
           isNewScrap(p);
  });

  const updatePrice = (idx, field, val) => {
    setPrices(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));
  };

  const handleSave = async () => {
    if (!hasChanges) { toast.info('No price changes'); return; }
    setSaving(true);
    try {
      const calls = [];
      prices.forEach((p, i) => {
        const e = entries[i];
        const rmChanged = p.raw_id && Number(p.rm_price) !== e.raw?.current_price;
        const scrapChanged = p.scrap_id && Number(p.scrap_price) !== e.scrap?.current_price;
        const newScrap = isNewScrap(p);
        if (rmChanged) {
          calls.push(api.put(`/materials/${p.raw_id}/price`, {
            price: Number(p.rm_price), reason,
            scrap_price: scrapChanged ? Number(p.scrap_price) : undefined,
            skip_scrap_adjust: scrapChanged || newScrap,
          }));
        } else if (scrapChanged) {
          calls.push(api.put(`/materials/${p.scrap_id}/price`, { price: Number(p.scrap_price), reason }));
        }
        if (newScrap) {
          calls.push(api.put('/materials/scrap-price', {
            category: e.category, grade: e.grade, price: Number(p.scrap_price),
            thickness_mm: e.thickness_mm ?? null, unit: e.raw?.unit || 'kg', reason,
          }));
        }
      });
      if (calls.length === 0) { toast.info('No price changes'); setSaving(false); return; }
      // Don't let one failing call hide the others — persist what works and refresh regardless.
      const results = await Promise.allSettled(calls);
      const failed = results.filter(r => r.status === 'rejected');
      if (results.length > failed.length) onUpdated();  // refresh whatever succeeded
      if (failed.length === 0) {
        toast.success('Prices updated');
        onClose();
      } else {
        toast.error(`${failed.length} of ${results.length} update${results.length !== 1 ? 's' : ''} failed: ${getErrMsg(failed[0].reason)}`);
      }
    } catch (err) { toast.error(getErrMsg(err)); } finally { setSaving(false); }
  };

  const anyLargeRmIncrease = prices.some((p, i) => {
    const e = entries[i];
    if (!p.raw_id || !e.raw?.current_price) return false;
    const pct = ((Number(p.rm_price) - e.raw.current_price) / e.raw.current_price) * 100;
    return pct > 5;
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">Update Market Prices — {grade}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-sm p-2">
            Updating the <strong>market price</strong> here does not change any part's locked costing price. It only updates the current market index used for variance analysis.
          </p>

          {prices.map((p, i) => {
            const e = entries[i];
            const label = e.thickness_mm ? `${e.thickness_mm}mm variant` : 'Generic (no thickness)';
            const rmChanged = p.raw_id && Number(p.rm_price) !== e.raw?.current_price;
            const scrapChanged = p.scrap_id && Number(p.scrap_price) !== e.scrap?.current_price;
            const rmPct = rmChanged ? (((Number(p.rm_price) - e.raw.current_price) / e.raw.current_price) * 100).toFixed(1) : null;
            const scrapPct = scrapChanged ? (((Number(p.scrap_price) - e.scrap.current_price) / e.scrap.current_price) * 100).toFixed(1) : null;
            return (
              <div key={i} className={`border rounded-sm p-3 space-y-3 ${rmChanged || scrapChanged ? 'border-orange-300 bg-orange-50/30' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{grade}</span>
                  {e.thickness_mm
                    ? <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">{label}</span>
                    : <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{label}</span>
                  }
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {e.raw && (
                    <div>
                      <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Market RM Price (₹/kg)</label>
                      <input type="number" step="0.01" value={p.rm_price}
                        onChange={ev => updatePrice(i, 'rm_price', ev.target.value)}
                        data-testid={`rm-price-input-${e.thickness_mm || 'generic'}`}
                        className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
                      {rmPct && (
                        <p className={`text-xs mt-0.5 font-mono ${Number(rmPct) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {Number(rmPct) > 0 ? '+' : ''}{rmPct}% from ₹{fmt(e.raw.current_price)}/kg
                        </p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-bold uppercase text-amber-600 mb-1 block">Scrap Price (₹/kg)</label>
                    <input type="number" step="0.01" value={p.scrap_price}
                      onChange={ev => updatePrice(i, 'scrap_price', ev.target.value)}
                      placeholder={e.scrap ? undefined : '0'}
                      data-testid={`scrap-price-input-${e.thickness_mm || 'generic'}`}
                      className="w-full border border-amber-300 rounded-sm h-9 px-3 text-sm font-mono bg-amber-50/50 focus:outline-none focus:ring-2 focus:ring-amber-400" />
                    {scrapPct && (
                      <p className={`text-xs mt-0.5 font-mono ${Number(scrapPct) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {Number(scrapPct) > 0 ? '+' : ''}{scrapPct}% from ₹{fmt(e.scrap.current_price)}/kg
                      </p>
                    )}
                    {!e.scrap && p.scrap_price !== '' && Number(p.scrap_price) > 0 && canEditScrap && (
                      <p className="text-[10px] mt-0.5 text-amber-500">New scrap price — will be added on save</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Reason / Notes</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Fortnightly market review" data-testid="price-reason-input"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>

          {anyLargeRmIncrease && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3">
              <TrendingUp size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">RM price increase &gt;5% will trigger margin alerts for affected parts.</p>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-price-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
              {saving ? 'Updating...' : 'Update Prices'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditMaterialModal({ entry, onClose, onUpdated }) {
  const [form, setForm] = useState({ category: entry.category || '', grade: entry.grade || '', unit: entry.raw?.unit || entry.scrap?.unit || 'kg', thickness_mm: entry.raw?.thickness_mm ?? entry.scrap?.thickness_mm ?? '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.category || !form.grade) { toast.error('Required'); return; }
    setSaving(true);
    try {
      const matId = entry.raw?.material_id || entry.scrap?.material_id;
      const thickness = form.thickness_mm !== '' ? Number(form.thickness_mm) : null;
      await api.put(`/materials/${matId}`, { ...form, thickness_mm: thickness });
      toast.success('Updated'); onUpdated(); onClose();
    } catch (err) { toast.error(getErrMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Edit Material</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Category *</label>
            <CategorySelect value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} testId="edit-mat-category" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Grade *</label>
            <input type="text" value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} data-testid="edit-mat-grade"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Thickness (mm) <span className="text-slate-400 font-normal normal-case">optional</span></label>
            <input type="number" step="0.1" min="0" value={form.thickness_mm} onChange={e => setForm(f => ({ ...f, thickness_mm: e.target.value }))} data-testid="edit-mat-thickness"
              placeholder="e.g. 0.6"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <p className="text-xs text-slate-400">Renaming updates linked scrap entries and FG parts.</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-edit-material-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddMaterialModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ category: '', grade: '', rm_price: '', scrap_price: '', unit: 'kg', thickness_mm: '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.category || !form.grade || !form.rm_price) { toast.error('Required'); return; }
    setSaving(true);
    try {
      const thickness = form.thickness_mm !== '' ? Number(form.thickness_mm) : null;
      await api.post('/materials', { category: form.category, grade: form.grade, current_price: Number(form.rm_price), unit: form.unit, material_type: 'raw', thickness_mm: thickness });
      if (form.scrap_price && Number(form.scrap_price) > 0) {
        await api.post('/materials', { category: form.category, grade: form.grade, current_price: Number(form.scrap_price), unit: form.unit, material_type: 'scrap', thickness_mm: thickness });
      }
      toast.success('Added'); onAdded(); onClose();
    } catch (err) { toast.error(getErrMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Add Material Grade</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Category *</label>
            <CategorySelect value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))} testId="mat-category-select" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Grade *</label>
            <input type="text" value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} data-testid="mat-grade-input" placeholder="e.g. AW-5754"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Thickness (mm) <span className="text-slate-400 font-normal normal-case">optional — for thickness-specific pricing</span></label>
            <input type="number" step="0.1" min="0" value={form.thickness_mm} onChange={e => setForm(f => ({ ...f, thickness_mm: e.target.value }))} data-testid="mat-thickness-input" placeholder="e.g. 0.6"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Market RM Price (₹/kg) *</label>
              <input type="number" step="0.01" value={form.rm_price} onChange={e => setForm(f => ({ ...f, rm_price: e.target.value }))} data-testid="mat-rm-price-input"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Scrap (₹/kg)</label>
              <input type="number" step="0.01" value={form.scrap_price} onChange={e => setForm(f => ({ ...f, scrap_price: e.target.value }))} data-testid="mat-scrap-price-input"
                className="w-full border border-amber-300 rounded-sm h-9 px-3 text-sm font-mono bg-amber-50/50 focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-material-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
              {saving ? 'Adding...' : 'Add Grade'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryModal({ entry, onClose }) {
  const rmHistory = entry.raw?.price_history || [];
  const scrapHistory = entry.scrap?.price_history || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-slate-900">Price History — {entry.grade}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {rmHistory.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase text-slate-500 mb-2">RM Price History</p>
              <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                {(() => { const rev = [...rmHistory].reverse(); return rev.map((h, i) => {
                  const older = rev[i + 1];
                  const chg = older ? h.price - older.price : null;
                  return (
                    <div key={i} className="flex items-center justify-between py-2 text-xs gap-2">
                      <span className="font-mono text-slate-900 font-bold w-[72px]">{fmtCur(h.price)}/kg</span>
                      <span className={`font-mono w-16 text-right ${chg == null ? 'text-slate-300' : chg > 0 ? 'text-red-500' : chg < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {chg == null ? '—' : `${chg > 0 ? '▲' : chg < 0 ? '▼' : ''}${fmtCur(Math.abs(chg))}`}
                      </span>
                      <span className="text-slate-500 flex-1 text-right">{h.date?.substring(0, 10)}</span>
                      <span className="text-slate-400 truncate max-w-[110px]">{h.updated_by}</span>
                    </div>
                  );
                }); })()}
              </div>
            </div>
          )}
          {scrapHistory.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase text-amber-600 mb-2">Scrap Price History</p>
              <div className="max-h-36 overflow-y-auto divide-y divide-slate-100">
                {[...scrapHistory].reverse().map((h, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-xs">
                    <span className="font-mono text-amber-700 font-bold">{fmtCur(h.price)}/kg</span>
                    <span className="text-slate-500">{h.date?.substring(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {rmHistory.length === 0 && scrapHistory.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No history yet</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Editable supplier-name tag for an RM grade, shown next to the grade name in the Price Index so
 *  the user knows which supplier to check when updating the market price. Stored on the material. */
function RmSupplierTag({ material, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(material?.supplier_name || '');
  const [saving, setSaving] = useState(false);
  if (!material) return null;
  const supplier = material.supplier_name;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/materials/${material.material_id}/supplier`, { supplier_name: val });
      onSaved?.(material.material_id, res.data.supplier_name);
      setEditing(false);
      toast.success('Supplier updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update supplier');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input autoFocus value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(supplier || ''); } }}
          placeholder="Supplier name" data-testid={`rm-supplier-input-${material.material_id}`}
          className="border border-slate-300 rounded-sm h-6 px-2 text-xs w-40 focus:outline-none focus:ring-1 focus:ring-slate-900" />
        <button onClick={save} disabled={saving} className="text-[10px] font-bold text-emerald-600 hover:text-emerald-800">{saving ? '…' : 'Save'}</button>
        <button onClick={() => { setEditing(false); setVal(supplier || ''); }} className="text-[10px] text-slate-400 hover:text-slate-600">Cancel</button>
      </span>
    );
  }
  if (supplier) {
    return (
      <button onClick={() => { if (canEdit) { setVal(supplier); setEditing(true); } }} disabled={!canEdit}
        title={canEdit ? 'Click to edit supplier' : `Supplier: ${supplier}`}
        data-testid={`rm-supplier-${material.material_id}`}
        className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 transition-colors max-w-[180px]">
        <Truck size={11} className="shrink-0" /> <span className="truncate">{supplier}</span>
      </button>
    );
  }
  return canEdit ? (
    <button onClick={() => { setVal(''); setEditing(true); }} data-testid={`rm-supplier-add-${material.material_id}`}
      title="Add supplier for this RM"
      className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 border border-dashed border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors">
      <Plus size={10} /> Supplier
    </button>
  ) : null;
}

/* ====================== MAIN COMPONENT ====================== */

export default function RawMaterials() {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('parts'); // 'parts' or 'master'
  const [groupBy, setGroupBy] = useState('grade'); // 'grade' or 'customer'
  const [sortByPending, setSortByPending] = useState(false); // grade view: oldest market-price update first
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [search, setSearch] = useState('');

  // RM Master state
  const [updateEntry, setUpdateEntry] = useState(null);
  const [historyEntry, setHistoryEntry] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState(new Set());
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [merging, setMerging] = useState(false);

  const canEdit = user?.role !== 'viewer';
  const applySupplier = (materialId, supplierName) =>
    setMaterials(ms => ms.map(m => m.material_id === materialId ? { ...m, supplier_name: supplierName } : m));

  const fetchData = async () => {
    try {
      const [matRes, partsRes] = await Promise.all([api.get('/materials'), api.get('/parts')]);
      setMaterials(matRes.data);
      setParts(partsRes.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const grouped = groupMaterials(materials);
  const totalGrades = Object.values(grouped).reduce((s, arr) => s + arr.length, 0);
  const allEntries = Object.values(grouped).flat();

  // Get all thickness variants for a given grade name
  const getGradeEntries = (grade) => allEntries.filter(e => e.grade === grade);

  // Build a lookup: grade+thickness → material entry
  const gradeMap = useMemo(() => {
    const m = {};
    allEntries.forEach(e => {
      const thicknessKey = e.thickness_mm ? `@${e.thickness_mm}mm` : '';
      m[`${e.grade}${thicknessKey}`] = e;
      // Also keep plain grade as fallback for parts without thickness
      if (!m[e.grade]) m[e.grade] = e;
    });
    return m;
  }, [materials]); // eslint-disable-line

  // Unique customers and grades for filters
  const customers = useMemo(() => [...new Set(parts.map(p => p.customer_name).filter(Boolean))].sort(), [parts]);
  const grades = useMemo(() => [...new Set(parts.map(p => p.material_grade).filter(Boolean))].sort(), [parts]);

  // Filtered parts
  const filtered = useMemo(() => {
    return parts.filter(p => {
      if (filterCustomer && p.customer_name !== filterCustomer) return false;
      if (filterGrade && p.material_grade !== filterGrade) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(p.part_name || '').toLowerCase().includes(s) && !(p.part_number || '').toLowerCase().includes(s) && !(p.material_grade || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [parts, filterCustomer, filterGrade, search]);

  // Is any GRADE SHOWN in the current list overdue (RM market price not updated in >1 month)?
  // Parts-based + RM-only so it matches the dashboard count and the sortable list exactly — the
  // button never promises a grade the Parts × RM view can't actually show.
  const hasPending = useMemo(() => {
    if (groupBy !== 'grade') return false;
    const grades = new Set(filtered.map(p => p.material_grade || 'Unknown'));
    for (const g of grades) {
      const e = gradeMap[g];
      const d = e ? lastUpdatedDate(e.raw?.price_history) : null;
      if (!d || (Date.now() - d.getTime()) / 86400000 > PENDING_THRESHOLD_DAYS) return true;
    }
    return false;
  }, [filtered, gradeMap, groupBy]);

  // Group filtered parts
  const groupedParts = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const key = groupBy === 'grade' ? (p.material_grade || 'Unknown') : (p.customer_name || 'Unknown');
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    const entries = Object.entries(groups);
    if (sortByPending && groupBy === 'grade') {
      // Oldest/most-overdue RM market-price update first; never-updated grades (ts 0) float to top.
      const ts = (g) => {
        const e = gradeMap[g];
        if (!e) return 0;
        const d = lastUpdatedDate(e.raw?.price_history);
        return d ? d.getTime() : 0;
      };
      return entries.sort((a, b) => ts(a[0]) - ts(b[0]));
    }
    return entries.sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupBy, sortByPending, gradeMap]);

  // Merge functions
  const toggleMergeSelect = (key) => {
    setMergeSelected(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  };
  const selectedEntries = allEntries.filter(e => {
    const thicknessKey = e.thickness_mm ? `@${e.thickness_mm}mm` : '';
    return mergeSelected.has(`${e.category}::${e.grade}${thicknessKey}`);
  });
  const handleMerge = async (targetKey) => {
    const targetEntry = allEntries.find(e => `${e.category}::${e.grade}` === targetKey);
    if (!targetEntry?.raw) { toast.error('No valid target'); return; }
    const sourceIds = selectedEntries.filter(e => `${e.category}::${e.grade}` !== targetKey).map(e => e.raw?.material_id).filter(Boolean);
    if (sourceIds.length === 0) { toast.error('Select at least 2 grades'); return; }
    setMerging(true);
    try {
      const res = await api.post('/materials/merge', { target_material_id: targetEntry.raw.material_id, source_material_ids: sourceIds });
      toast.success(`Merged into ${res.data.target_grade}: ${res.data.parts_updated} parts updated`);
      setMergeMode(false); setMergeSelected(new Set()); setShowMergeDialog(false); fetchData();
    } catch (err) { toast.error(getErrMsg(err)); } finally { setMerging(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete ${entry.grade}?`)) return;
    try {
      if (entry.raw) await api.delete(`/materials/${entry.raw.material_id}`);
      if (entry.scrap) await api.delete(`/materials/${entry.scrap.material_id}`);
      toast.success('Deleted'); fetchData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="raw-materials-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">Raw Material Price Index</h2>
          <p className="text-slate-500 text-sm mt-0.5">{totalGrades} RM grades &middot; {parts.length} parts</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-sm text-slate-600 text-sm hover:bg-slate-50 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
          {canEdit && (
            <button onClick={() => setShowAdd(true)} data-testid="add-material-open-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 shadow-sm transition-colors">
              <Plus size={15} /> Add Grade
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-0.5 w-fit" data-testid="rm-tabs">
        <button onClick={() => setTab('parts')} data-testid="tab-parts-view"
          className={`px-4 py-1.5 text-sm font-semibold rounded-sm transition-colors ${tab === 'parts' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          Parts &times; RM
        </button>
        <button onClick={() => setTab('master')} data-testid="tab-master-view"
          className={`px-4 py-1.5 text-sm font-semibold rounded-sm transition-colors ${tab === 'master' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          RM Master
        </button>
      </div>

      {/* ===================== PARTS × RM TAB ===================== */}
      {tab === 'parts' && (
        <>
          {/* Filters & Group By */}
          <div className="flex items-center gap-3 flex-wrap" data-testid="parts-rm-filters">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts..."
                data-testid="rm-parts-search"
                className="w-full border border-slate-300 rounded-sm h-9 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} data-testid="filter-customer"
              className="border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white">
              <option value="">All Customers</option>
              {customers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)} data-testid="filter-grade"
              className="border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white">
              <option value="">All RM Grades</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-0.5">
              <button onClick={() => setGroupBy('grade')} data-testid="group-by-grade"
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-sm transition-colors ${groupBy === 'grade' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                <Layers size={11} /> By Grade
              </button>
              <button onClick={() => setGroupBy('customer')} data-testid="group-by-customer"
                className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-sm transition-colors ${groupBy === 'customer' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                <Users size={11} /> By Customer
              </button>
            </div>
            <button onClick={() => setSortByPending(v => { const nv = !v; if (nv) setGroupBy('grade'); return nv; })}
              data-testid="sort-pending-updates"
              title="Bring the RM grades with the oldest market-price update to the top"
              className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold rounded-sm border transition-colors ${
                sortByPending ? 'bg-amber-500 text-white border-amber-500'
                : hasPending ? 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
              <Clock size={13} /> Pending Market Price Updates{sortByPending ? ' ✓' : ''}
            </button>
            {(search || filterCustomer || filterGrade) && (
              <button onClick={() => { setSearch(''); setFilterCustomer(''); setFilterGrade(''); }}
                className="text-slate-400 hover:text-slate-700 text-sm flex items-center gap-1"><X size={13} /> Clear</button>
            )}
          </div>

          {/* Grouped Parts Table */}
          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-slate-200 rounded-sm animate-pulse" />)}</div>
          ) : groupedParts.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No parts found</div>
          ) : (
            groupedParts.map(([group, groupParts]) => {
              const thicknessKey = groupBy === 'grade' && group !== 'Unknown' ? (() => {
                // Try to find the best matching entry (prefer thickness-specific if parts have a common thickness)
                const thicknesses = [...new Set(groupParts.map(p => p.thickness_mm).filter(Boolean))];
                if (thicknesses.length === 1) {
                  const key = `${thicknesses[0]}mm`;
                  if (gradeMap[`${group}@${key}`]) return `@${key}`;
                }
                return '';
              })() : '';
              const matEntry = groupBy === 'grade' ? (gradeMap[`${group}${thicknessKey}`] || gradeMap[group] || null) : null;
              return (
                <div key={group} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                  {/* Group Header */}
                  <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {groupBy === 'grade' && matEntry && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${CATEGORY_COLORS[matEntry.category] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                          {matEntry.category}
                        </span>
                      )}
                      <span className="font-mono font-bold text-slate-800">{group}</span>
                      <span className="text-xs text-slate-500">{groupParts.length} part{groupParts.length !== 1 ? 's' : ''}</span>
                      {groupBy === 'grade' && matEntry && (
                        <RmSupplierTag material={matEntry.raw || matEntry.scrap} canEdit={canEdit} onSaved={applySupplier} />
                      )}
                    </div>
                    {groupBy === 'grade' && matEntry && (
                      <div className="flex items-center gap-4 text-xs">
                        <span className="font-mono font-bold text-slate-900">RM: {matEntry.raw ? fmtCur(matEntry.raw.current_price) + '/kg' : '—'}</span>
                        <span className="font-mono font-semibold text-amber-700">Scrap: {matEntry.scrap ? fmtCur(matEntry.scrap.current_price) + '/kg' : '—'}</span>
                        <PriceTrendBadge history={matEntry.raw?.price_history} />
                        <div style={{ width: 60 }}><MiniSparkline history={matEntry.raw?.price_history} /></div>
                        <button onClick={() => setHistoryEntry(matEntry)} title="Price history"
                          data-testid={`view-history-${matEntry.grade}`}
                          className="flex items-center gap-1 px-2 py-1 text-blue-600 border border-blue-200 rounded-sm text-xs font-semibold hover:bg-blue-50 transition-colors">
                          <History size={11} /> History
                        </button>
                        {canEdit && (
                          <div className="flex flex-col items-end gap-1">
                            <button onClick={() => setUpdateEntry({ grade: matEntry.grade, entries: getGradeEntries(matEntry.grade) })} data-testid={`update-price-${matEntry.grade}`}
                              className="flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-600 border border-orange-200 rounded-sm text-xs font-semibold hover:bg-orange-100 transition-colors">
                              <TrendingUp size={11} /> Update Market Price
                            </button>
                            <LastUpdatedBadge history={matEntry.raw?.price_history} entry={matEntry} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Parts Table */}
                  <div className="overflow-auto max-h-[70vh]">
                    <table className="data-table w-full min-w-[750px]">
                      <thead>
                        <tr>
                          <th className="text-left">Part Name</th>
                          <th className="text-left">Part No.</th>
                          {groupBy !== 'customer' && <th className="text-left">Customer</th>}
                          {groupBy !== 'grade' && <th className="text-left">RM Grade</th>}
                          <th className="text-right">Locked RM/kg</th>
                          <th className="text-right">Market RM/kg</th>
                          <th className="text-right">Locked Scrap/kg</th>
                          <th className="text-right">Market Scrap/kg</th>
                          <th className="text-right">Gross Wt</th>
                          <th className="text-right">Sell Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupParts.map(p => {
                          const locked = p.rm_price_per_kg || p.rm_variance?.locked_price || 0;
                          const market = p.market_rm_price || p.rm_variance?.market_price || 0;
                          const hasVariance = locked > 0 && market > 0 && locked !== market;
                          const lockedScrap = p.scrap_price_per_kg || p.scrap_variance?.locked_price || 0;
                          const marketScrap = p.market_scrap_price || p.scrap_variance?.market_price || 0;
                          const scrapStatus = p.scrap_variance?.status;
                          return (
                          <tr key={p.part_id} className="group">
                            <td className="font-semibold text-slate-800 max-w-[200px] truncate">{p.part_name || '—'}</td>
                            <td className="font-mono text-xs text-slate-600">{p.part_number}</td>
                            {groupBy !== 'customer' && <td className="text-xs text-slate-600">{p.customer_name}</td>}
                            {groupBy !== 'grade' && (
                              <td className="text-xs">
                                <span className="font-mono text-slate-700">{p.material_grade}</span>
                                <span className="text-slate-400 ml-1">({p.material_category})</span>
                              </td>
                            )}
                            <td className="text-right font-mono text-sm font-bold text-slate-900">
                              {fmtCur(locked)}
                            </td>
                            <td className="text-right">
                              {market > 0 ? (
                                <span className={`font-mono text-sm font-semibold ${hasVariance && market > locked ? 'text-red-600' : hasVariance && market < locked ? 'text-green-600' : 'text-slate-600'}`}>
                                  {fmtCur(market)}
                                </span>
                              ) : <span className="text-xs text-slate-300">—</span>}
                            </td>
                            <td className="text-right font-mono text-sm font-semibold text-amber-700">
                              {lockedScrap > 0 ? fmtCur(lockedScrap) : <span className="text-xs text-slate-300">—</span>}
                            </td>
                            <td className="text-right">
                              {marketScrap > 0 ? (
                                <span className={`font-mono text-sm font-semibold ${scrapStatus === 'better' ? 'text-green-600' : scrapStatus === 'worse' ? 'text-red-600' : 'text-slate-600'}`}>
                                  {fmtCur(marketScrap)}
                                  {scrapStatus === 'better' && <span className="ml-1 text-[9px] font-bold text-green-500">▲</span>}
                                  {scrapStatus === 'worse' && <span className="ml-1 text-[9px] font-bold text-red-500">▼</span>}
                                </span>
                              ) : <span className="text-xs text-slate-300">—</span>}
                            </td>
                            <td className="text-right font-mono text-xs text-slate-600">{fmt(p.gross_weight)} kg</td>
                            <td className="text-right font-mono text-sm font-semibold text-slate-800">{fmtCur(p.selling_price)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {/* ===================== RM MASTER TAB ===================== */}
      {tab === 'master' && (
        <>
          {/* Merge bar */}
          <div className="flex items-center gap-2">
            {canEdit && (
              <button onClick={() => { setMergeMode(!mergeMode); setMergeSelected(new Set()); }} data-testid="merge-mode-btn"
                className={`flex items-center gap-1.5 px-3 py-2 border rounded-sm text-sm font-semibold transition-colors ${
                  mergeMode ? 'bg-violet-50 text-violet-700 border-violet-300' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}>
                <GitMerge size={14} /> {mergeMode ? 'Cancel Merge' : 'Merge Grades'}
              </button>
            )}
            {mergeMode && mergeSelected.size >= 2 && (
              <button onClick={() => setShowMergeDialog(true)} data-testid="open-merge-dialog-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white text-sm font-bold rounded-sm hover:bg-violet-700 transition-colors">
                <GitMerge size={13} /> Merge {mergeSelected.size} Selected
              </button>
            )}
            {mergeMode && <span className="text-sm text-violet-600">{mergeSelected.size === 0 ? 'Select grades to merge' : `${mergeSelected.size} selected`}</span>}
          </div>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-sm animate-pulse" />)}</div>
          ) : (
            Object.entries(grouped).map(([category, entries]) => (
              <div key={category} className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-3">
                  <CategoryHeader category={category} canEdit={canEdit} onRenamed={fetchData} />
                  <span className="text-xs text-slate-500">{entries.length} grade{entries.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="overflow-auto max-h-[70vh]">
                  <table className="data-table w-full">
                    <thead>
                      <tr>
                        {mergeMode && <th style={{ width: '40px' }}></th>}
                        <th className="text-left">Grade</th>
                        <th className="text-right">Market RM Price</th>
                        <th className="text-right">Market Scrap Price</th>
                        <th className="text-right">Linked Parts</th>
                        <th className="text-right">30d Change</th>
                        <th className="text-left" style={{ width: '80px' }}>Trend</th>
                        {!mergeMode && <th className="text-center">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(entry => {
                        const rmHistory = entry.raw?.price_history || [];
                        const thicknessKey = entry.thickness_mm ? `@${entry.thickness_mm}mm` : '';
                        const entryKey = `${entry.category}::${entry.grade}${thicknessKey}`;
                        const isSelected = mergeSelected.has(entryKey);
                        const linkedCount = parts.filter(p => p.material_grade === entry.grade).length;
                        return (
                          <tr key={`${entry.category}-${entry.grade}-${entry.thickness_mm || 'generic'}`}
                            className={`group ${isSelected ? 'bg-violet-50' : ''}`}
                            onClick={mergeMode ? () => toggleMergeSelect(entryKey) : undefined}
                            style={mergeMode ? { cursor: 'pointer' } : undefined}>
                            {mergeMode && (
                              <td className="text-center">
                                <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                  isSelected ? 'bg-violet-600 border-violet-600' : 'border-slate-300'
                                }`}>
                                  {isSelected && <Check size={12} className="text-white" />}
                                </div>
                              </td>
                            )}
                            <td><span className="font-mono font-bold text-slate-800">{entry.grade}</span>{(entry.raw?.thickness_mm || entry.scrap?.thickness_mm) && <span className="ml-1.5 text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">{entry.raw?.thickness_mm || entry.scrap?.thickness_mm}mm</span>}</td>
                            <td className="text-right font-mono text-base font-black text-slate-900">
                              {entry.raw ? <>{fmtCur(entry.raw.current_price)}<span className="text-xs font-normal text-slate-400">/kg</span></> : '—'}
                            </td>
                            <td className="text-right font-mono text-sm font-bold text-amber-700">
                              {entry.scrap ? <>{fmtCur(entry.scrap.current_price)}<span className="text-xs font-normal text-amber-400">/kg</span></> : '—'}
                            </td>
                            <td className="text-right">
                              <button onClick={(e) => { e.stopPropagation(); setFilterGrade(entry.grade); setTab('parts'); setGroupBy('grade'); }}
                                disabled={linkedCount === 0}
                                className={`text-xs font-semibold transition-colors ${linkedCount > 0 ? 'text-blue-600 hover:text-blue-800 hover:underline cursor-pointer' : 'text-slate-300 cursor-default'}`}>
                                {linkedCount} part{linkedCount !== 1 ? 's' : ''}
                              </button>
                            </td>
                            <td className="text-right"><PriceTrendBadge history={rmHistory} /></td>
                            <td style={{ width: '80px', padding: '4px 8px' }}><MiniSparkline history={rmHistory} /></td>
                            {!mergeMode && (
                              <td className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={() => setHistoryEntry(entry)} title="History" data-testid={`view-history-${entry.grade}`}
                                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-sm transition-colors"><History size={13} /></button>
                                  {canEdit && (
                                    <>
                                      <button onClick={() => setEditEntry(entry)} title="Edit" data-testid={`edit-material-${entry.grade}`}
                                        className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-sm transition-colors"><Edit size={13} /></button>
                                      <div className="flex flex-col items-center gap-1">
                                        <button onClick={() => setUpdateEntry({ grade: entry.grade, entries: getGradeEntries(entry.grade) })} data-testid={`update-price-${entry.grade}-${entry.thickness_mm || 'generic'}`}
                                          className="flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-600 border border-orange-200 rounded-sm text-xs font-semibold hover:bg-orange-100 transition-colors">
                                          <TrendingUp size={11} /> Update Market Price
                                        </button>
                                        <LastUpdatedBadge history={rmHistory} />
                                      </div>
                                      <button onClick={() => handleDelete(entry)} title="Delete" data-testid={`delete-material-${entry.grade}`}
                                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm transition-colors"><Trash2 size={13} /></button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* Modals */}
      {showAdd && <AddMaterialModal onClose={() => setShowAdd(false)} onAdded={fetchData} />}
      {editEntry && <EditMaterialModal entry={editEntry} onClose={() => setEditEntry(null)} onUpdated={fetchData} />}
      {updateEntry && <PriceUpdateModal grade={updateEntry.grade} entries={updateEntry.entries} onClose={() => setUpdateEntry(null)} onUpdated={fetchData} />}
      {historyEntry && <HistoryModal entry={historyEntry} onClose={() => setHistoryEntry(null)} />}

      {/* Merge Dialog */}
      {showMergeDialog && (
        <Dialog open onOpenChange={() => setShowMergeDialog(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-900 flex items-center gap-2">
                <GitMerge size={18} className="text-violet-600" /> Merge Grades
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">
                Pick the <strong>canonical grade</strong> to keep. Others will be merged into it.
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {selectedEntries.map(entry => {
                  const key = `${entry.category}::${entry.grade}`;
                  const cnt = parts.filter(p => p.material_grade === entry.grade).length;
                  return (
                    <button key={key} onClick={() => handleMerge(key)} disabled={merging} data-testid={`merge-target-${entry.grade}`}
                      className="w-full text-left p-3 border border-slate-200 rounded-sm hover:border-violet-400 hover:bg-violet-50 transition-colors group disabled:opacity-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-mono font-bold text-slate-800">{entry.grade}</p>
                          <p className="text-xs text-slate-500">{entry.category} &middot; {entry.raw ? fmtCur(entry.raw.current_price) + '/kg' : 'N/A'} &middot; {cnt} parts</p>
                        </div>
                        <span className="text-xs font-semibold text-violet-600 opacity-0 group-hover:opacity-100 transition-opacity">Keep this &rarr;</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {merging && <p className="text-sm text-violet-600 font-semibold animate-pulse">Merging...</p>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
